import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { ContactDocument, GhlContact, GhlContext, PandaContact, PandaTemplate, Subcontractor } from '../types';
import { templateSection, type TemplateSection } from '../config/templateCategory';
import { canRevertToDraft, isCompleted, isDraft, pandaDocUrl, statusLabel } from '../config/docStatus';

type Props = {
  ghl: GhlContext;
  initialContactId: string | null;
  onDocumentCreated: (documentId: string) => void;
};

// The picker's top-level mode. 'customer'/'contractor' filter the template list;
// 'upload' swaps the template list for a file drop zone (upload any file → sign,
// no template).
type Section = TemplateSection | 'upload';

export function Picker({ ghl, initialContactId, onDocumentCreated }: Props) {
  const [section, setSection] = useState<Section>('customer');
  const [contact, setContact] = useState<GhlContact | null>(null);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState<GhlContact[]>([]);
  const [templates, setTemplates] = useState<PandaTemplate[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [docs, setDocs] = useState<ContactDocument[]>([]);
  // Contractor section only: the subcontractor signer, picked from PandaDoc contacts.
  const [sub, setSub] = useState<PandaContact | null>(null);
  const [subSearch, setSubSearch] = useState('');
  const [subResults, setSubResults] = useState<PandaContact[]>([]);
  // Upload section only: the file to send for signature + an optional signer email.
  const [file, setFile] = useState<File | null>(null);
  const [signerEmail, setSignerEmail] = useState('');
  const [uploading, setUploading] = useState(false);
  // Client-side filters so long lists don't need scrolling.
  const [docQuery, setDocQuery] = useState('');
  const [tplQuery, setTplQuery] = useState('');
  // "Edit" on a sent document: which row shows the confirm box, and which one
  // is being pulled back into draft right now.
  const [revertAsk, setRevertAsk] = useState<string | null>(null);
  const [reverting, setReverting] = useState<string | null>(null);
  // A refusal ("already signed by …") shows in that row, not in the page-top
  // banner, which is scrolled out of sight by the time the rep reaches the list.
  const [revertNote, setRevertNote] = useState<{ documentId: string; message: string } | null>(null);

  // Pre-load the contact if we got an id from the URL (deep-link).
  useEffect(() => {
    if (!initialContactId) return;
    api.getContact(initialContactId, ghl).then(setContact).catch((e) => setError(String(e)));
  }, [initialContactId, ghl]);

  // Always load templates (small list).
  useEffect(() => {
    api.listTemplates(ghl).then(setTemplates).catch((e) => setError(String(e)));
  }, [ghl]);

  // Load the contact's existing PandaDoc documents (full history).
  useEffect(() => {
    if (!contact) { setDocs([]); return; }
    api.listContactDocuments(contact.id, ghl).then(setDocs).catch((e) => setError(String(e)));
  }, [contact, ghl]);

  // Debounced contact search.
  useEffect(() => {
    if (!contactSearch.trim()) {
      setContactResults([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .searchContacts(contactSearch, ghl)
        .then(setContactResults)
        .catch((e) => setError(String(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [contactSearch, ghl]);

  // Debounced subcontractor search (PandaDoc contacts) — Contractor section only.
  useEffect(() => {
    if (section !== 'contractor' || !subSearch.trim()) {
      setSubResults([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .listPandaContacts(subSearch, ghl)
        .then(setSubResults)
        .catch((e) => setError(String(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [subSearch, section, ghl]);

  // Upload section: default the signer to the selected customer, so picking the
  // customer actually routes the uploaded document to them. The create-from-file
  // call only attaches a recipient when we pass one — with no signer the document
  // is created with zero recipients, which is what made an upload "send to no one"
  // (and let PandaDoc's editor fall back to a stray last-used contact). Stays
  // editable: a rep sending an uploaded file to a subcontractor just types a
  // different email. Re-defaults when the customer changes or the tab reopens.
  useEffect(() => {
    if (section !== 'upload') return;
    setSignerEmail(contact?.email ?? '');
  }, [contact, section]);

  const visibleTemplates = templates.filter((t) => templateSection(t) === section);

  // Live client-side filtering of the two lists (no backend call).
  const shownTemplates = tplQuery.trim()
    ? visibleTemplates.filter((t) => t.name.toLowerCase().includes(tplQuery.trim().toLowerCase()))
    : visibleTemplates;
  const visibleDocs = docQuery.trim()
    ? docs.filter((d) =>
        `${d.name} ${d.status.replace('document.', '')} ${d.dateCreated || ''}`
          .toLowerCase()
          .includes(docQuery.trim().toLowerCase()),
      )
    : docs;

  async function pickTemplate(template: PandaTemplate) {
    if (!contact) return;
    if (section === 'contractor' && !sub) {
      setError('Pick a subcontractor first.');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const subcontractor: Subcontractor | undefined =
        section === 'contractor' && sub
          ? { email: sub.email, firstName: sub.firstName, lastName: sub.lastName, company: sub.company, phone: sub.phone }
          : undefined;
      const result = await api.createDocument(
        { templateId: template.id, contactId: contact.id, subcontractor },
        ghl,
      );
      // Real create failures now surface as a thrown error carrying PandaDoc's
      // actual rejection reason (backend responds HTTP 502 { error }). A truthy
      // result with no documentId is only an unexpected empty response — report
      // it plainly instead of guessing at a roles/mapping cause.
      if (!result || !result.documentId) {
        throw new Error(
          `Couldn't create a document from "${template.name}" — the backend returned no document id ` +
            `and no error detail. Check the n8n execution log for this run.`,
        );
      }
      onDocumentCreated(result.documentId);
    } catch (e) {
      setError(String(e));
    } finally {
      setCreating(false);
    }
  }

  // "Edit" on a sent/viewed document: PandaDoc locks it once sent, so pull it
  // back into draft first, then open the same editor as "Continue editing".
  async function revertAndEdit(doc: ContactDocument) {
    setReverting(doc.documentId);
    setRevertNote(null);
    try {
      const result = await api.revertToDraft(doc.documentId, ghl);
      if (isDraft(result.status)) {
        onDocumentCreated(doc.documentId);
        return;
      }
      setRevertNote({
        documentId: doc.documentId,
        message: 'Back in draft, but PandaDoc is still processing it. Wait a few seconds, then press Continue editing.',
      });
    } catch (e) {
      setRevertNote({ documentId: doc.documentId, message: e instanceof Error ? e.message : String(e) });
    } finally {
      setReverting(null);
      setRevertAsk(null);
    }
    // Only reached when the editor did not open: show the row's real status.
    if (contact) api.listContactDocuments(contact.id, ghl).then(setDocs).catch(() => undefined);
  }

  // Upload section: create a PandaDoc document from the chosen file (no template),
  // then hand off to the same embedded editor (place signature, assign signer, send).
  async function uploadFile() {
    if (!contact || !file) return;
    setUploading(true);
    setError(null);
    try {
      const customer = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email || 'Customer';
      const base = file.name.replace(/\.[^.]+$/, '');
      // "Customer - filename": the Drive router derives the client folder from the
      // part before the first "-", so this keeps auto-filing working.
      const docName = `${customer} - ${base}`;
      // Default signer is the selected customer (email pre-filled from them). If
      // the rep typed a different address (e.g. a subcontractor), send just the
      // email; attach the customer's name only when the signer is still the
      // customer, so the recipient shows a real name rather than a bare email.
      const trimmed = signerEmail.trim();
      const isCustomer = trimmed !== '' && trimmed.toLowerCase() === (contact.email ?? '').trim().toLowerCase();
      const signer = trimmed
        ? { email: trimmed, firstName: isCustomer ? contact.firstName : undefined, lastName: isCustomer ? contact.lastName : undefined }
        : undefined;
      const result = await api.createDocumentFromFile(file, { contactId: contact.id, docName, signer }, ghl);
      if (!result || !result.documentId) {
        throw new Error(
          "Uploaded, but the backend returned no document id and no error detail. " +
            'Check the n8n execution log for this run.',
        );
      }
      onDocumentCreated(result.documentId);
    } catch (e) {
      setError(String(e));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
      {/* Section switch */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #e5e7eb' }}>
        {(['customer', 'contractor', 'upload'] as const).map((s) => (
          <button
            key={s}
            onClick={() => { setSection(s); setError(null); setTplQuery(''); }}
            style={{
              padding: '8px 14px',
              background: 'transparent',
              border: 'none',
              borderBottom: section === s ? '2px solid #2563eb' : '2px solid transparent',
              color: section === s ? '#2563eb' : '#6b7280',
              fontWeight: section === s ? 600 : 500,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {s === 'customer' ? 'Customer documents' : s === 'contractor' ? 'Contractor documents' : 'Upload a file'}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
          1. {section === 'contractor' ? 'Customer / job' : 'Customer'}
        </h2>
        {contact ? (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6 }}>
            <div>
              <div style={{ fontWeight: 500 }}>{contact.firstName} {contact.lastName}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>{contact.email} · {contact.phone}</div>
            </div>
            <button onClick={() => { setContact(null); setContactSearch(''); setDocQuery(''); }} style={btnGhost}>Change</button>
          </div>
        ) : (
          <>
            <input
              autoFocus
              value={contactSearch}
              onChange={(e) => setContactSearch(e.target.value)}
              placeholder="Search by name or email…"
              style={input}
            />
            {contactResults.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, marginTop: 8, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, maxHeight: 240, overflow: 'auto' }}>
                {contactResults.map((c) => (
                  <li key={c.id}>
                    <button onClick={() => setContact(c)} style={{ ...btnPlain, width: '100%', textAlign: 'left', padding: 12 }}>
                      <div style={{ fontWeight: 500 }}>{c.firstName} {c.lastName}</div>
                      <div style={{ fontSize: 13, color: '#6b7280' }}>{c.email}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {contactResults.length >= 50 && (
              <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 6 }}>
                Showing the first 50 matches — keep typing to narrow it down.
              </p>
            )}
          </>
        )}
      </section>

      {/* Contractor section: pick the subcontractor signer from PandaDoc contacts. */}
      {section === 'contractor' && (
        <section style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>2. Subcontractor</h2>
          {sub ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6 }}>
              <div>
                <div style={{ fontWeight: 500 }}>{sub.company || `${sub.firstName ?? ''} ${sub.lastName ?? ''}`.trim()}</div>
                <div style={{ fontSize: 13, color: '#6b7280' }}>{sub.email}</div>
              </div>
              <button onClick={() => { setSub(null); setSubSearch(''); }} style={btnGhost}>Change</button>
            </div>
          ) : (
            <>
              <input
                value={subSearch}
                onChange={(e) => setSubSearch(e.target.value)}
                placeholder="Search subcontractors (name, company, email)…"
                style={input}
              />
              {subResults.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, marginTop: 8, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, maxHeight: 240, overflow: 'auto' }}>
                  {subResults.map((c) => (
                    <li key={c.id ?? c.email}>
                      <button onClick={() => setSub(c)} style={{ ...btnPlain, width: '100%', textAlign: 'left', padding: 12 }}>
                        <div style={{ fontWeight: 500 }}>{c.company || `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.email}</div>
                        <div style={{ fontSize: 13, color: '#6b7280' }}>{c.email}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}

      {contact && docs.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
            Documents for this contact ({visibleDocs.length}{visibleDocs.length !== docs.length ? ` of ${docs.length}` : ''})
          </h2>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <input
              value={docQuery}
              onChange={(e) => setDocQuery(e.target.value)}
              placeholder="Filter documents by name, status or date…"
              style={{ ...input, paddingRight: docQuery ? 60 : 10 }}
            />
            {docQuery && (
              <button onClick={() => setDocQuery('')} style={{ position: 'absolute', right: 6, top: 5, ...btnGhost, padding: '4px 10px' }}>Clear</button>
            )}
          </div>
          {visibleDocs.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: 13 }}>No documents match “{docQuery.trim()}”.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
              {/* Only DRAFTS open in the editor. PandaDoc locks a document once it
                  is sent, and the editor then fails with "could not load this
                  document" — so an unsigned sent doc offers "Edit", which pulls
                  it back into draft first; signed docs link out to PandaDoc. */}
              {visibleDocs.map((d) => (
                <li
                  key={d.documentId}
                  style={{ padding: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6 }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {statusLabel(d.status)}{d.dateCreated ? ` · ${d.dateCreated.slice(0, 10)}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flex: '0 0 auto' }}>
                      {isDraft(d.status) ? (
                        <button onClick={() => onDocumentCreated(d.documentId)} style={btnGhost}>
                          Continue editing
                        </button>
                      ) : (
                        <>
                          {canRevertToDraft(d.status) && (
                            <button
                              disabled={reverting !== null}
                              onClick={() => { setRevertNote(null); setRevertAsk(revertAsk === d.documentId ? null : d.documentId); }}
                              style={btnGhost}
                            >
                              Edit
                            </button>
                          )}
                          {isCompleted(d.status) && (
                            <button
                              onClick={() => api.downloadDocument(d.documentId, ghl).catch((e) => setError(String(e)))}
                              style={{ ...btnGhostLink, cursor: 'pointer' }}
                            >
                              PDF
                            </button>
                          )}
                          <a href={pandaDocUrl(d.documentId)} target="_blank" rel="noreferrer" style={btnGhostLink}>
                            View ↗
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Inline confirm, not window.confirm(): the app lives in GHL's
                      iframe, where browser dialogs may be blocked. */}
                  {revertAsk === d.documentId && (
                    <div style={confirmBox}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Return this document to draft to edit it?</div>
                      <ul style={{ margin: '0 0 10px', paddingLeft: 18, color: '#4b5563' }}>
                        <li>Recipients can no longer sign the current version. Nobody is notified now.</li>
                        <li>After editing, press Send — the recipients get the updated document by email.</li>
                        <li>QuickBooks marks this document’s estimate Rejected; sending again creates a new one.</li>
                      </ul>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          disabled={reverting !== null}
                          onClick={() => revertAndEdit(d)}
                          style={{ ...primaryBtn, padding: '6px 12px', fontSize: 13, opacity: reverting ? 0.6 : 1 }}
                        >
                          {reverting === d.documentId ? 'Returning to draft…' : 'Return to draft & edit'}
                        </button>
                        <button disabled={reverting !== null} onClick={() => setRevertAsk(null)} style={btnGhost}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  {revertNote?.documentId === d.documentId && (
                    <div style={{ ...confirmBox, background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' }}>
                      {revertNote.message}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {section === 'upload' ? (
        <section>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>2. File to sign</h2>
          {!contact ? (
            <p style={{ color: '#9ca3af', fontSize: 13 }}>Pick a customer first.</p>
          ) : (
            <>
              <label style={dropzone}>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                  style={{ display: 'none' }}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                {file ? (
                  <span style={{ fontWeight: 500 }}>{file.name}</span>
                ) : (
                  <span style={{ color: '#6b7280' }}>Click to choose a file — PDF, DOCX or image</span>
                )}
              </label>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', margin: '12px 0 4px' }}>
                Who signs it
              </label>
              <input
                value={signerEmail}
                onChange={(e) => setSignerEmail(e.target.value)}
                placeholder="Signer email"
                style={input}
              />
              <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 6 }}>
                {contact?.email
                  ? `Defaults to the customer (${contact.email}). Change it to send to a subcontractor or someone else.`
                  : 'This customer has no email on file — type the signer’s email, or add them in the editor.'}
              </p>
              <button
                disabled={!file || uploading}
                onClick={uploadFile}
                style={{ ...primaryBtn, marginTop: 12, opacity: !file || uploading ? 0.6 : 1 }}
              >
                {uploading ? 'Uploading…' : 'Open in editor'}
              </button>
              <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 8 }}>
                Opens in the PandaDoc editor — place the Signature field where it goes, then Send.
                The signer above already receives it; the signed PDF files into this customer’s
                “Subs Work Orders” folder automatically.
              </p>
            </>
          )}
        </section>
      ) : (
        <section>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
            {section === 'contractor' ? '3. Template' : '2. Template'}
          </h2>
          {!contact ? (
            <p style={{ color: '#9ca3af', fontSize: 13 }}>Pick a customer first.</p>
          ) : section === 'contractor' && !sub ? (
            <p style={{ color: '#9ca3af', fontSize: 13 }}>Pick a subcontractor first.</p>
          ) : visibleTemplates.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: 13 }}>No templates in this section.</p>
          ) : (
            <>
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <input
                  value={tplQuery}
                  onChange={(e) => setTplQuery(e.target.value)}
                  placeholder="Filter templates by name…"
                  style={{ ...input, paddingRight: tplQuery ? 60 : 10 }}
                />
                {tplQuery && (
                  <button onClick={() => setTplQuery('')} style={{ position: 'absolute', right: 6, top: 5, ...btnGhost, padding: '4px 10px' }}>Clear</button>
                )}
              </div>
              {shownTemplates.length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: 13 }}>No templates match “{tplQuery.trim()}”.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
                  {shownTemplates.map((t) => (
                    <li key={t.id}>
                      <button
                        disabled={creating}
                        onClick={() => pickTemplate(t)}
                        style={{ ...btnPlain, width: '100%', textAlign: 'left', padding: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6 }}
                      >
                        {t.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}

const input: React.CSSProperties = {
  width: '100%',
  padding: 10,
  border: '1px solid #d1d5db',
  borderRadius: 6,
  fontSize: 14,
};

const btnGhost: React.CSSProperties = {
  padding: '6px 12px',
  background: 'transparent',
  border: '1px solid #e5e7eb',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
};

const btnPlain: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 14,
};

const btnGhostLink: React.CSSProperties = {
  padding: '6px 12px',
  background: 'transparent',
  color: '#374151',
  border: '1px solid #e5e7eb',
  borderRadius: 4,
  fontSize: 13,
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

const confirmBox: React.CSSProperties = {
  marginTop: 10,
  padding: 12,
  background: '#fffbeb',
  border: '1px solid #fde68a',
  borderRadius: 6,
  fontSize: 13,
};

const dropzone: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '28px 16px',
  border: '1px dashed #cbd5e1',
  borderRadius: 8,
  background: 'white',
  cursor: 'pointer',
  fontSize: 14,
};

const primaryBtn: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 6,
  border: 'none',
  background: '#2563eb',
  color: 'white',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};
